import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { fetchTasks, createTask, updateTask, fetchAssignedTaskDetails } from './actions'
import { TaskState, Task, TaskFilters, TaskDateRange } from './types'

// YTD default: from January 1 of the current year to today
const getYTDDateRange = (): TaskDateRange => {
  const now = new Date()
  const startOfYear = new Date(now.getFullYear(), 0, 1)
  return {
    startDate: startOfYear.toISOString(),
    endDate: now.toISOString(),
  }
}

const initialState: TaskState = {
  loading: false,
  loadingFetchAssignedTasks: false,
  loadingAssignTask: false,
  loadingCreateTask: false,
  loadingUpdateTask: false,
  loadingUpdateAssignedTask: false,

  updateLoading: false,
  selectedTaskLoading: false,
  selectedTaskError: null,
  loadingUpdateAssignedTeamMember: false,
  errorUpdateAssignedTeamMember: '',

  filters: {
    importance: [],
    operationalManager: [],
    supervisor: [],
    functionalArea: [],
  },

  dateRange: getYTDDateRange(),

  tasks: [],
  teamMemberTasks: [], // This is correct as TeamMemberTask[]
  assignedTask: [], // This is correct as Task[]
  selectedTask: {} as Task,
  searchQuery: '',
  error: null,

  errorFetchAssignedTasks: '',
  errorAssignTask: '',
  errorCreateTask: '',
  errorUpdateAssignedTask: '',
  errorUpdateTask: '',
}

const manageTasksSlice = createSlice({
  name: 'manageTasks',
  initialState,
  reducers: {
    clearSelectedTask: (state) => {
      state.selectedTask = {} as Task
    },
    clearError: (state) => {
      state.error = null
    },
    setFilters: (state, action: PayloadAction<TaskFilters>) => {
      state.filters = action.payload
    },
    setSearchQuery: (state, action: PayloadAction<string>) => {
      state.searchQuery = action.payload
    },
    setDateRange: (state, action: PayloadAction<TaskDateRange>) => {
      state.dateRange = action.payload
    },
    resetDateRange: (state) => {
      state.dateRange = getYTDDateRange()
    },
    resetFilters: (state) => {
      state.filters = {
        importance: [],
        operationalManager: [],
        supervisor: [],
        functionalArea: [],
      }
      state.searchQuery = ''
      state.dateRange = getYTDDateRange()
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTasks.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchTasks.fulfilled, (state, action) => {
        state.loading = false
        state.tasks = action.payload
      })
      .addCase(fetchTasks.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to fetch tasks'
      })

      // Add task
      .addCase(createTask.pending, (state) => {
        state.loadingCreateTask = true
        state.errorCreateTask = ''
      })
      .addCase(createTask.fulfilled, (state, action) => {
        state.loadingCreateTask = false
        state.tasks.push(action.payload)
      })
      .addCase(createTask.rejected, (state, action) => {
        state.loadingCreateTask = false
        state.errorCreateTask = action.error.message || 'Error creating task'
      })

      // Update task
      .addCase(updateTask.pending, (state) => {
        state.loadingUpdateTask = true
        state.errorUpdateTask = ''
      })
      .addCase(updateTask.fulfilled, (state, action) => {
        state.loadingUpdateTask = false
        const index = state.tasks.findIndex((task) => task.id === action.payload.id)
        if (index !== -1) {
          state.tasks[index] = action.payload
        }
      })
      .addCase(updateTask.rejected, (state, action) => {
        state.loadingUpdateTask = false
        state.errorUpdateTask = action.error.message || 'Error updating task'
      })

      // Assign task
      .addCase(fetchAssignedTaskDetails.pending, (state) => {
        state.loadingFetchAssignedTasks = true
        state.errorFetchAssignedTasks = ''
      })
      .addCase(fetchAssignedTaskDetails.fulfilled, (state, action) => {
        state.loadingFetchAssignedTasks = false
        state.assignedTask = Array.isArray(action.payload) ? action.payload : [action.payload]
      })
      .addCase(fetchAssignedTaskDetails.rejected, (state, action) => {
        state.loadingFetchAssignedTasks = false
        state.errorFetchAssignedTasks = action.error.message || 'Failed to fetch assigned tasks'
      })
  },
})

export const { setSearchQuery, setFilters, setDateRange, resetDateRange, resetFilters } = manageTasksSlice.actions
export default manageTasksSlice.reducer
